import { useTranslation } from 'react-i18next';
import OrgNumberInput from '_common/components/input/OrgNumberInput';
import CheckboxWithLabel from '_common/components/checkbox/CheckboxWithLabel';
import TermsLink from '_common/components/terms-link/TermsLink';
import {
  validateEmail,
  validatePassword,
  validatePasswordRepeat
} from 'utils/validation';
import FormField from '_common/components/input/FormField';
import SpacedContainer from '_common/components/container/FormContainer.style';
import { OrganizationAPIResponse } from '_common/hooks/useCreateRole';
import { DeliveryAddressField } from '../../../components/delivery-address-field/DeliveryAddressField';

type SignupConsumerFieldsProps = {
  name?: string;
  contactPerson?: string;
  deliveryAddress?: string;
  phone?: string;
  orgNumber?: string;
  email?: string;
  password?: string;
  passwordRepeat?: string;
  howDidYouHearAboutUs?: string;
  acceptTerms?: boolean;
  validationErrorField?: string;
  isLoggedIn?: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  organization: OrganizationAPIResponse;
  onOrganizationChanged: (org: OrganizationAPIResponse) => void;
};

const SignupConsumerFields = ({
  name = '',
  contactPerson = '',
  deliveryAddress = '',
  phone = '',
  orgNumber = '',
  email = '',
  password = '',
  passwordRepeat = '',
  howDidYouHearAboutUs = '',
  acceptTerms = false,
  validationErrorField = '',
  isLoggedIn = false,
  onChange,
  organization,
  onOrganizationChanged
}: SignupConsumerFieldsProps) => {
  const { t } = useTranslation();

  return (
    <SpacedContainer>
      <OrgNumberInput
        value={orgNumber}
        validationErrorField={validationErrorField}
        onChange={onChange}
        organization={organization}
        onOrganizationChanged={onOrganizationChanged}
      />
      <FormField
        labelText={t('signup:companyLabelText')}
        type="text"
        placeholder={t('signup:companyPlaceholder')}
        success={name.length > 1}
        error={validationErrorField === 'name'}
        name="name"
        value={name}
        onChange={onChange}
      />
      <FormField
        labelText={t('signup:contactPersonLabelText')}
        type="text"
        placeholder={t('signup:contactPersonPlaceholder')}
        success={contactPerson.length > 1}
        error={validationErrorField === 'contactPerson'}
        name="contactPerson"
        value={contactPerson}
        onChange={onChange}
      />
      <DeliveryAddressField
        deliveryAddress={deliveryAddress}
        onDeliveryAddressChange={(address: string) => {
          const event: React.ChangeEvent<HTMLInputElement> = {
            target: { name: 'deliveryAddress', value: address }
          } as React.ChangeEvent<HTMLInputElement>;
          onChange(event);
        }}
      />
      <FormField
        labelText={t('signup:phoneLabelText')}
        type="text"
        placeholder={t('signup:phonePlaceholder')}
        success={phone.length >= 8}
        error={validationErrorField === 'phone'}
        name="phone"
        value={phone}
        onChange={onChange}
      />
      {!isLoggedIn && (
        <>
          <FormField
            labelText={t('signup:emailLabelText')}
            type="text"
            placeholder={t('signup:emailPlaceholder')}
            success={validateEmail(email)}
            warning={Boolean(email && !validateEmail(email))}
            error={validationErrorField === 'email'}
            name="email"
            value={email}
            onChange={onChange}
            autoComplete="email"
          />
          <FormField
            labelText={t('signup:passwordLabelText')}
            type="password"
            placeholder={t('signup:passwordPlaceholder')}
            success={validatePassword(password)}
            warning={Boolean(password && !validatePassword(password))}
            error={validationErrorField === 'password'}
            name="password"
            value={password}
            onChange={onChange}
            autoComplete="new-password"
          />
          <FormField
            labelText={t('signup:passwordRepeatLabelText')}
            type="password"
            placeholder={t('signup:passwordRepeatPlaceholder')}
            success={Boolean(
              passwordRepeat && validatePasswordRepeat(password, passwordRepeat)
            )}
            warning={Boolean(
              passwordRepeat &&
                !validatePasswordRepeat(password, passwordRepeat)
            )}
            error={validationErrorField === 'passwordRepeat'}
            name="passwordRepeat"
            value={passwordRepeat}
            onChange={onChange}
            autoComplete="new-password"
          />
          <FormField
            labelText={t('signup:howDidYouHearAboutUsLabelText')}
            type="text"
            placeholder={t('signup:howDidYouHearAboutUsPlaceholder')}
            success={howDidYouHearAboutUs.length > 1}
            name="howDidYouHearAboutUs"
            value={howDidYouHearAboutUs}
            onChange={onChange}
          />
        </>
      )}
      <CheckboxWithLabel
        label={
          <>
            {t('signup:acceptTermsAccept')}{' '}
            <TermsLink>{t('signup:acceptTermsTermsLink')}</TermsLink>
          </>
        }
        checked={acceptTerms}
        name="acceptTerms"
        onChange={onChange}
      />
    </SpacedContainer>
  );
};

export default SignupConsumerFields;
